import React from "react";
import { BookingValues } from "./stateMachine/types/bookingValues";
import { BookingTypeStep } from "./steps/BookingTypeStep";
import { NameAndDepotStepWithLookup } from "./steps/NameAndDepotStepWithLookup";
import { NewDepotStep } from "./steps/NewDepotStep";
import { PersistedStep } from "./steps/PersistedStep";
import { PriceAndDateStep } from "./steps/PriceAndDateStep";
import { SomethingWrong } from "./steps/SomethingWrong";
import { SummaryStep } from "./steps/SummaryStep";
import { SendEventType } from "./stateMachine/types/SendEventType";
import { StateType } from "./stateMachine/types/StateType";
import { BookingTypeWithSplit } from "./stateMachine/types/splitValues";

export interface Props {
  currentState: StateType;
  sendEvent: SendEventType;
  initFromSearch?: boolean;
  isManualTransaction?: boolean;
  initFromNewTransaction?: boolean;
  depotId?: number;
}

export const NewTransactionSteps = ({
  currentState,
  sendEvent,
  initFromSearch,
  initFromNewTransaction,
  isManualTransaction,
  depotId,
}: Props) => {
  const backOneStep = () => sendEvent({ type: "BACK" });
  const backFromBookingTypeStep = () => {
    initFromSearch || (isManualTransaction && !initFromNewTransaction)
      ? sendEvent({ type: "BACK" })
      : window.history.back();
  };

  return (
    <>
      {currentState.matches("selectBookingType") && (
        <BookingTypeStep
          defaultValue={currentState.context.bookingValues.type}
          investmentType={
            currentState.context.investmentValues.type || "11_stock"
          }
          handleNext={(bookingType: BookingTypeWithSplit) => {
            if (bookingType === "split")
              throw new Error("split is an invalid type for new booking");
            sendEvent({
              type: "BOOKING_TYPE_SELECTED",
              bookingType: bookingType,
            });
          }}
          handlePrevious={backFromBookingTypeStep}
          isNewInvestment
        />
      )}

      {currentState.matches("depots.selectDepot") && (
        <NameAndDepotStepWithLookup
          handleNext={(depotId: number, name: string, comment?: string) =>
            sendEvent({
              type: "NAME_AND_DEPOT_SELECTED",
              depotId: depotId,
              name: name,
              comment: comment,
            })
          }
          handleNewDepot={(name: string, comment?: string) =>
            sendEvent({
              type: "CREATE_NEW_DEPOT",
              name: name,
              comment: comment,
            })
          }
          handlePrevious={backOneStep}
          defaultName={currentState.context.investmentValues.name || ""}
          defaultComment={currentState.context.bookingValues.comment}
          selectedDepotId={currentState.context.investmentValues.depotId}
          selectableDepots={currentState.context.selectableDepots || []}
          isin={currentState.context.investmentValues?.isin}
          tickerSymbol={currentState.context.investmentValues?.tickerSymbol}
          quoteProvider={currentState.context.investmentValues?.quoteProvider}
        />
      )}

      {currentState.matches("depots.createNewDepot") && (
        <NewDepotStep
          handleNext={(name: string, currency: string) =>
            sendEvent({
              type: "PERSIST_DEPOT",
              name: name,
              currency: currency,
            })
          }
          handlePrevious={backOneStep}
        />
      )}

      {currentState.matches("priceAndDate") && (
        <PriceAndDateStep
          handleNext={(bookingValues: BookingValues, currency?: string) =>
            sendEvent({
              type: "VALIDATE_BOOKING",
              bookingValues,
              currency,
            })
          }
          symbol={currentState.context.symbol}
          handlePrevious={backOneStep}
          bookingValues={currentState.context.bookingValues}
          investmentValues={currentState.context.investmentValues}
          validationError={currentState.context.validationError}
        />
      )}

      {currentState.matches("summary") && (
        <SummaryStep
          handleNext={() =>
            sendEvent({
              type: "PERSIST",
            })
          }
          error={currentState.context.error}
          handlePrevious={backOneStep}
          bookingValues={currentState.context.bookingValues}
          investmentType={
            currentState.context.investmentValues.type || "11_stock"
          }
          investmentCurrency={
            currentState.context.investmentValues.investmentCurrency || "EUR"
          }
          quoteNotation={currentState.context.investmentValues.quoteNotation}
        />
      )}

      {currentState.matches("persisted") && (
        <PersistedStep
          redirectUrl={depotId ? `/depots/${depotId}` : "/investments"}
          investmentName={currentState.context.investmentValues.name || ""}
        />
      )}

      {currentState.matches("somethingWrong") && (
        <SomethingWrong message={currentState.context.error?.message} />
      )}
    </>
  );
};
